import { type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Button variant definitions built with `class-variance-authority`.
 *
 * ### Variants
 * | variant        | description                                            |
 * |----------------|--------------------------------------------------------|
 * | `default`      | Solid accent gradient (pink → purple)                  |
 * | `outline`      | Transparent with accent border — used for answer pills |
 * | `success`      | Green gradient — "Buy Up" actions                      |
 * | `danger`       | Red gradient — "Buy Down" actions                      |
 * | `ghost`        | No background or border — inline actions               |
 *
 * ### Sizes
 * | size   | height | usage                        |
 * |--------|--------|------------------------------|
 * | `sm`   | 32px   | Compact / secondary actions  |
 * | `md`   | 38px   | Default — answer pills, CTAs |
 * | `lg`   | 42px   | Featured answer buttons      |
 * | `icon` | 40px   | Square icon-only buttons     |
 */
const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-1.5',
    'font-sans font-semibold cursor-pointer',
    'transition-opacity hover:opacity-90 active:opacity-80',
    'disabled:pointer-events-none disabled:opacity-50',
  ].join(' '),
  {
    variants: {
      variant: {
        default: [
          'bg-gradient-to-b from-accent to-accent-dark text-white',
          'rounded-tab border-2 border-accent',
          'shadow-card',
          '[box-shadow:inset_0px_2.8px_5.6px_rgba(255,255,255,0.25)]',
        ].join(' '),
        outline: ['bg-transparent border-[1.5px] border-accent', 'rounded-pill text-primary'].join(' '),
        success: [
          'bg-gradient-to-b from-success to-success-dark text-white',
          'rounded-[19px] shadow-card',
          '[box-shadow:inset_0px_2px_2px_rgba(255,255,255,0.5)]',
          '[text-shadow:1px_1px_0_rgba(0,0,0,0.25)]',
        ].join(' '),
        danger: [
          'bg-gradient-to-b from-danger to-danger-dark text-white',
          'rounded-[19px] shadow-card',
          '[box-shadow:inset_0px_2px_2px_rgba(255,255,255,0.5)]',
          '[text-shadow:1px_1px_0_rgba(0,0,0,0.25)]',
        ].join(' '),
        ghost: 'bg-transparent border-none text-primary',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-[38px] px-4 text-base',
        lg: 'h-[42px] px-[17px] text-lg',
        icon: 'size-10 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & VariantProps<typeof buttonVariants>;

export { buttonVariants };
